import type {
  MySubscriptionResponse,
  Plan,
  SubscriptionDetail,
} from '@/lib/api-client';

/**
 * Helpers for the two-plan lineup (Free + a single paid plan offered at two
 * intervals). The backend returns the paid plan as two rows sharing
 * `tier !== 'free'`, distinguished by `interval`. The UI presents them as one
 * plan with a monthly/annual toggle.
 */

export type BillingInterval = 'month' | 'year';

/**
 * Feature flag for the no-card 7-day free trial. OFF for now — the trial needs
 * more testing before rollout, so its entry points (onboarding pricing step +
 * Settings billing) are hidden. Flip to `true` to re-enable everywhere.
 * (Typed `boolean` so gated JSX doesn't trip no-unnecessary-condition.)
 */
export const TRIAL_ENABLED: boolean = false;

export interface GroupedPlans {
  free?: Plan;
  monthly?: Plan;
  annual?: Plan;
}

export function groupPlans(plans: Plan[] | undefined): GroupedPlans {
  const list = plans ?? [];
  return {
    free: list.find(p => p.tier === 'free' || p.amount_cents === 0),
    monthly: list.find(p => p.tier !== 'free' && p.interval === 'month'),
    annual: list.find(p => p.tier !== 'free' && p.interval === 'year'),
  };
}

/** The paid plan for a given interval, if it exists in the catalog. */
export function paidPlanFor(
  grouped: GroupedPlans,
  interval: BillingInterval
): Plan | undefined {
  return interval === 'year' ? grouped.annual : grouped.monthly;
}

/** Whole-dollar string, e.g. 1499 -> "$14.99", 15588 -> "$155.88". */
export function formatUsd(amountCents: number): string {
  return `$${(amountCents / 100).toFixed(2)}`;
}

/** Per-month equivalent of an annual price, e.g. 15588 -> "$12.99". */
export function perMonthFromAnnual(annualCents: number): string {
  return `$${(annualCents / 12 / 100).toFixed(2)}`;
}

/**
 * First charge for a plan. When the backend surfaces an auto-applied promo (the
 * real Stripe coupon behind the annual "XONERA" code) we use its first-period
 * price; otherwise the list price. No discount amount is hardcoded client-side.
 */
export function effectiveFirstChargeCents(plan: Plan): number {
  return plan.promo ? plan.promo.first_period_amount_cents : plan.amount_cents;
}

/**
 * The first renewal of a plan bought on `from`: one billing interval later,
 * on the same day of the month - or the month's last day when it has no such
 * day, as Stripe bills it (bought Jan 31 → Feb 28; Feb 29 yearly → Feb 28).
 * Plain `setMonth` overflowed instead: Jan 31 + 1 month read "Mar 3".
 */
export function firstRenewalDate(plan: Plan, from: Date = new Date()): Date {
  const next = new Date(from);
  next.setDate(1);
  next.setMonth(next.getMonth() + (plan.interval === 'year' ? 12 : 1));
  const lastDay = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0
  ).getDate();
  next.setDate(Math.min(from.getDate(), lastDay));
  return next;
}

/**
 * The Billing-history badge for an invoice status. The backend only lists
 * bills (paid, or a renewal / plan-change charge still owed), so the two
 * unpaid states are said plainly rather than in Stripe's words.
 */
export function invoiceStatusBadge(status: string | null | undefined): {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'neutral';
} {
  switch (status) {
    case 'paid':
      return { label: 'Paid', variant: 'success' };
    case 'open':
      return { label: 'Payment due', variant: 'warning' };
    case 'uncollectible':
      return { label: 'Failed', variant: 'error' };
    default:
      return { label: status ?? '-', variant: 'neutral' };
  }
}

/**
 * What checkout commits the buyer to, in one sentence beside Pay: today's
 * charge and the recurring one after it, e.g. "You'll be charged $14.99 today,
 * then $14.99 every month from Nov 11, 2026 until you cancel."
 */
export function recurringChargeTerms(
  plan: Plan,
  from: Date = new Date()
): string {
  const today = formatUsd(effectiveFirstChargeCents(plan));
  const firstPeriod = plan.promo ? ' for your first year' : '';
  const renewsOn = firstRenewalDate(plan, from).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return (
    `You'll be charged ${today} today${firstPeriod}, then ` +
    `${formatUsd(plan.amount_cents)} every ${plan.interval} from ${renewsOn} ` +
    'until you cancel.'
  );
}

/**
 * The yearly plan's "Save N%" badge: its first-year charge against paying
 * monthly for a year (12 x monthly) - the same comparison the card makes by
 * striking the monthly price through next to the yearly per-month price.
 * e.g. monthly 1499, yearly 15588 with XONERA (first year 11988) -> 33;
 * without a promo -> 13. Null when either plan is missing or nothing is saved.
 */
export function annualPromoSavingsPct(
  annual: Plan | undefined,
  monthly: Plan | undefined
): number | null {
  if (!annual || !monthly || monthly.amount_cents <= 0) return null;
  const pct = Math.round(
    (1 - effectiveFirstChargeCents(annual) / (monthly.amount_cents * 12)) * 100
  );
  return pct > 0 ? pct : null;
}

export type ChangeDirection =
  | 'noop'
  | 'downgrade_to_free'
  | 'upgrade_immediate'
  | 'downgrade_scheduled';

const INTERVAL_RANK: Record<string, number> = { month: 1, year: 2 };

/**
 * Classify a plan switch to mirror the backend (`_classify_change`), so the UI
 * can show the right confirm copy: upgrades apply immediately (prorated),
 * downgrades apply at period end (no charge today).
 *
 * - month → year   = upgrade (immediate)
 * - year → month   = downgrade (scheduled at period end)
 * - paid → free    = downgrade to free (cancel at period end)
 */
export function classifyChange(current: Plan, target: Plan): ChangeDirection {
  if (target.id === current.id) return 'noop';
  if (target.tier === 'free' || target.amount_cents === 0)
    return 'downgrade_to_free';
  if (current.tier === 'free') return 'upgrade_immediate';
  const curRank = INTERVAL_RANK[current.interval] ?? 1;
  const tgtRank = INTERVAL_RANK[target.interval] ?? 1;
  if (tgtRank !== curRank)
    return tgtRank > curRank ? 'upgrade_immediate' : 'downgrade_scheduled';
  return target.amount_cents >= current.amount_cents
    ? 'upgrade_immediate'
    : 'downgrade_scheduled';
}

// ── Display ──────────────────────────────────────────────────────────

const INTERVAL_NAME: Record<string, string> = {
  month: 'Monthly',
  year: 'Yearly',
};

function isFreePlan(plan: Plan): boolean {
  return plan.tier === 'free' || plan.amount_cents === 0;
}

/**
 * The name a plan goes by in words: "Free", "Plus · Monthly", "Plus · Yearly".
 *
 * The two paid rows share a catalog name, so `plan.name` alone made every
 * button, summary and toast about them read "Plus" — a user switching between
 * the two could not tell which one a button meant.
 */
export function planDisplayName(plan: Plan): string {
  if (isFreePlan(plan)) return plan.name;
  const interval =
    INTERVAL_NAME[plan.interval] ??
    plan.interval.charAt(0).toUpperCase() + plan.interval.slice(1);
  return `${plan.name} · ${interval}`;
}

export interface PlanPriceLines {
  /** The headline figure, e.g. "$9.99". */
  price: string;
  /** Rendered after a slash: "/mo", "/month", "/forever". */
  unit: string;
  /** The monthly plan's price, struck through beside the yearly one. */
  strikePrice?: string;
  /** The saving against paying monthly, e.g. "Save $60 in your first year". */
  savings?: string;
  /** What is billed and what it renews at, e.g. "$119.88 billed yearly · then $12.99/mo". */
  sub?: string;
}

/** "$60" for whole dollars, "$60.50" otherwise. */
function formatUsdShort(amountCents: number): string {
  return amountCents % 100 === 0
    ? `$${amountCents / 100}`
    : formatUsd(amountCents);
}

/**
 * Every price string a plan card shows. The yearly plan leads with its
 * first-year per-month price against the monthly price (struck through), names
 * the saving in dollars - above $100 a dollar figure reads bigger than a
 * percentage, and the toggle already carries "Save N%" - and puts the billing
 * facts in one quiet line, renewal included, so the discount reads as
 * first-year only.
 */
export function planPriceLines(
  plan: Plan,
  grouped: GroupedPlans
): PlanPriceLines {
  if (isFreePlan(plan)) return { price: '$0', unit: 'forever' };
  if (plan.interval !== 'year') {
    return { price: formatUsd(plan.amount_cents), unit: plan.interval };
  }
  const firstCharge = effectiveFirstChargeCents(plan);
  const lines: PlanPriceLines = {
    price: perMonthFromAnnual(firstCharge),
    unit: 'mo',
    sub: plan.promo
      ? `${formatUsd(firstCharge)} billed yearly · then ${perMonthFromAnnual(
          plan.amount_cents
        )}/mo`
      : `${formatUsd(plan.amount_cents)} billed yearly`,
  };
  if (grouped.monthly) {
    lines.strikePrice = formatUsd(grouped.monthly.amount_cents);
    const saved = grouped.monthly.amount_cents * 12 - firstCharge;
    if (saved > 0) {
      lines.savings = plan.promo
        ? `Save ${formatUsdShort(saved)} in your first year`
        : `Save ${formatUsdShort(saved)} a year`;
    }
  }
  return lines;
}

// ── What a subscriber pays ───────────────────────────────────────────

export interface SubscriptionPricing {
  /** Billed for the current period, after the period's discount. */
  periodCents: number;
  /** The plan's catalog price. */
  listCents: number;
  /** Billed at the next renewal. */
  renewalCents: number;
  /** The code the period was billed with, when the discount came from one. */
  code: string | null;
  /** The discount ends at renewal — a first-period promo like XONERA. */
  introductory: boolean;
}

/**
 * The current period's price and the renewal's, from the discount the server
 * mirrored off the period's invoice (`subscription.discount`). The plan's own
 * `promo` is not used: it says what a NEW checkout would get, not what this
 * subscriber was billed.
 */
export function subscriptionPricing(
  plan: Plan,
  subscription: Pick<SubscriptionDetail, 'discount'>
): SubscriptionPricing {
  const discount = subscription.discount ?? null;
  const listCents = plan.amount_cents;
  if (!discount || discount.amount_off_cents <= 0) {
    return {
      periodCents: listCents,
      listCents,
      renewalCents: listCents,
      code: null,
      introductory: false,
    };
  }
  const periodCents = Math.max(0, listCents - discount.amount_off_cents);
  return {
    periodCents,
    listCents,
    renewalCents: discount.applies_to_renewal ? periodCents : listCents,
    code: discount.code,
    introductory: !discount.applies_to_renewal,
  };
}

export interface CurrentPlanPriceLines {
  /** The headline figure: what this period cost. */
  price: string;
  /** Beside it: "/year", or "first year" for an introductory price. */
  unit: string;
  /** Per-month equivalent of what this period cost (annual plans only). */
  perMonth?: string;
  /** The code, and what the price becomes when the promo ends. */
  promo?: string;
  /** The amount the next renewal charges, for "Renews <date> at <amount>". */
  renewalAmount: string;
}

/** The Settings plan card's price lines for a live subscription. */
export function currentPlanPriceLines(
  plan: Plan,
  subscription: Pick<SubscriptionDetail, 'discount'>
): CurrentPlanPriceLines {
  const pricing = subscriptionPricing(plan, subscription);
  const annual = plan.interval === 'year';
  const discounted = pricing.periodCents !== pricing.listCents;
  const lines: CurrentPlanPriceLines = {
    price: formatUsd(pricing.periodCents),
    unit: pricing.introductory ? `first ${plan.interval}` : `/${plan.interval}`,
    renewalAmount: formatUsd(pricing.renewalCents),
  };
  if (annual) {
    lines.perMonth = pricing.introductory
      ? `${perMonthFromAnnual(pricing.periodCents)}/mo this year`
      : `${perMonthFromAnnual(pricing.periodCents)}/mo, billed yearly`;
  }
  if (discounted) {
    const applied = pricing.code
      ? `Code ${pricing.code} applied`
      : 'Discount applied';
    lines.promo = pricing.introductory
      ? `${pricing.code ? `Code ${pricing.code}` : applied} · then ${formatUsd(
          pricing.listCents
        )}/${plan.interval}`
      : applied;
  }
  return lines;
}

export interface PurchaseSummary {
  /** "Plus · Yearly". */
  planName: string;
  /** What the checkout charged. */
  chargedCents: number;
  /** What the next renewal charges. */
  renewalCents: number;
  /** ISO timestamp of the renewal; null until the subscription has synced. */
  renewsAt: string | null;
}

/**
 * The just-bought plan, for the onboarding Complete step. Prefers the synced
 * subscription (the invoice's real discount and period end) and falls back to
 * the plan the checkout sold — its promo is exactly what was charged — while
 * the mirror has not caught up yet.
 */
export function purchaseSummary(
  bought: Plan,
  mine: Pick<MySubscriptionResponse, 'plan' | 'subscription'> | undefined
): PurchaseSummary {
  const plan = mine?.plan ?? null;
  const subscription = mine?.subscription ?? null;
  if (plan && subscription) {
    const pricing = subscriptionPricing(plan, subscription);
    return {
      planName: planDisplayName(plan),
      chargedCents: pricing.periodCents,
      renewalCents: pricing.renewalCents,
      renewsAt: subscription.current_period_end,
    };
  }
  return {
    planName: planDisplayName(bought),
    chargedCents: effectiveFirstChargeCents(bought),
    renewalCents:
      bought.promo?.duration === 'forever'
        ? effectiveFirstChargeCents(bought)
        : bought.amount_cents,
    renewsAt: null,
  };
}

/**
 * What moving from `paid` to `lower` takes away, from the catalog's own
 * feature lists: the paid plan's bullets the lower plan does not have, minus
 * heading rows like "Everything in Free, plus:". Empty when the catalog has
 * no features to compare — the caller supplies its own sentence then.
 */
export function featuresLostOnDowngrade(paid: Plan, lower: Plan): string[] {
  const kept = new Set(lower.features);
  return paid.features.filter(f => !kept.has(f) && !f.trim().endsWith(':'));
}
